import { BallooningProject } from "./Ballooning";

export interface AqlTableEntry {
	id: string;
	range_start: number;
	range_end: number;
	sample_size: number;
}

export interface AqlTable {
	id: string;
	name: string;
	description: string | null;
	entries: AqlTableEntry[];
}

export interface GageQuery {
	page: number;
	search: string;
	ordering: string;
}

export interface GageCategory {
	id: string;
	name: string;
}

export interface GageType {
	id: string;
	name: string;
	category: GageCategory;
	is_fastlane_gage: boolean;
	measurement_strategy: string;
	measurement_type: string;
	is_pass_fail_gage: boolean;
}

export interface GageCalibrationDocument {
	id: string;
	calibration: string; // Assuming this is the ID of the GageCalibration
	document: string; // URL or path to the document
}

export interface GageCalibration {
	id: string;
	calibration_date: string;
	calibrated_by: string;
	notes: string | null;
	documents: GageCalibrationDocument[];
}

export interface Building {
	id: string;
	name: string;
}

export interface Gage {
	id: string;
	type: GageType;
	building: Building | null;
	make: string;
	model: string;
	purchase_date: string | null; // ISO date string; null when not provided
	calibrations: GageCalibration[] | null;
	mfg_serial_number: string;
	internal_serial_number: string;
	status: string | null;
	location: string | null;
	description: string | null;
	calibration_due_date: string | null;
	calibration_status: string | null;
	effective_calibration_status: string | null;
	most_recent_calibration_date: string | null;
	range: string | null;
	removal_reason?: string | null;
	manufacturer_calibration_date: string | null;
	calibration_strategy: string | null;
	manufacturer_calibration_certificate?: string;
}

export interface PaginatedGageResponse {
	count: number;
	next: string | null;
	previous: string | null;
	results: Gage[];
}

export interface InspectionSample {
	id: string;
	visual_inspection_status: string;
	sample_number: number;
	status: string;
	from_sample: InspectionResult[];
	hand_quality_status: string;
	hand_completed_by?: string | null;
	hand_completed_at?: string | null;
	keyence_status: string;
	recent_report?: KeyenceReport[];
	report?: KeyenceReport;
	view?: string;
	order_line_item?: string | null;
	// quality_status of the batch this sample belongs to (resolved server-side,
	// mirroring the input gate). Used to disable inputs per batch on a Swiss-lot
	// shared plan instead of locking every batch. PTB-7023.
	batch_quality_status?: string | null;
}


export interface KeyenceReport {
	id?: string,
	view: string,
	file_name: string,
	overriden?: boolean,
	linked?: boolean,
	deleted?: boolean,
	judgment?: string,
	measurement_time?: string,
	linked_by?: string | null,
	linked_at?: string | null,
}

type KeyenceCsvCell = number | string | null;

/** One measured feature (balloon) of a Keyence CSV, from the T150 proxy. */
export interface KeyenceSampleReportFeature {
	name: string;
	design: KeyenceCsvCell;
	upper: KeyenceCsvCell;
	lower: KeyenceCsvCell;
	value: KeyenceCsvCell;
	in_tolerance: boolean | null;
}

export interface KeyenceSampleReport {
	file_name: string;
	judgment: KeyenceCsvCell;
	serial_counter: KeyenceCsvCell;
	measurement_time: KeyenceCsvCell;
	features: KeyenceSampleReportFeature[];
}

export interface SampleViewItem {
	label: string;
	value: string;
	sample_id: string;
	sample_number?: number;
	  view: string;
  device_name?: string;
  report: KeyenceReport | null;
	recent_report?: KeyenceReport[];
	batch_quality_status?: string | null;
}

export interface InspectionResult {
	id: string;
	balloon: string;
	value?: number;
	passed?: boolean;
	source: string;
	gage?: string | null;
	gage_internal_serial?: string | null;
	gage_calibration_status_at_use?: string | null;
	gage_calibration_due_date_at_use?: string | null;
	gage_has_calibration_certificate_at_use?: boolean | null;
}

export interface InspectionPlan {
	id: string;
	project: BallooningProject;
	samples: InspectionSample[];
	passed_samples: number;
	failed_samples: number;
	total_samples: number;
	name: string;
	keyence_status: string;
	hand_quality_status: string;
	status: string;
	next_sample: InspectionSample | undefined;
	view_devices: { view_name: string; device_name: string }[];
	views: string[];
	sample_view_list: SampleViewItem[];
	has_keyence_measurements?: boolean;
	requires_inspection_sample_labeling?: boolean;
}

export type DeviceSyncStatus = 'Synced' | 'Pending' | 'Failed';

export interface KeyenceProgram {
	id: string,
	file_name: string,
	view: string;
    uploaded_by: number;
	uploaded_by_name: string;
	created_time: string;
    version: number | null;
    keyence_device_name?: string | null;
    sqfx_file?: string;
    /** Whether the file actually reached the Keyence device. A program uploaded
     *  while the proxy was down stays 'Pending' until someone pushes it. */
    device_sync_status?: DeviceSyncStatus;
    device_synced_at?: string | null;
    device_sync_error?: string;
    /** Failed pushes since the last success. 'Pending' retries in the
     *  background on a backoff; 'Failed' means the budget ran out. */
    device_sync_attempts?: number;
    device_sync_next_attempt_at?: string | null;
    /** Upload responses only: the check run on this file as it was uploaded.
     *  Null when validation is off or the file duplicated the current version. */
    validation?: KeyenceValidationSummary | null;
}

export interface KeyenceSystem {
	id: string;
	name: string;
	file_extension?: string;
	building?: Building | null;
}

export interface PaginatedInspectionPlanResponse {
	count: number;
	next: string | null;
	previous: string | null;
	results: InspectionPlan[];
}

export type KeyenceValidationSeverity = 'Error' | 'Warning' | 'Info';

export type KeyenceValidationOutcome =
	| 'Passed'
	/** No errors, but warnings someone should review; never blocks an upload. */
	| 'Warning'
	| 'Failed'
	| 'Skipped'
	| 'Parse Error'
	/** Too large to parse on the request; a worker is filling in the verdict. */
	| 'Pending';

export type KeyenceValidationEnforcement =
	| 'Accepted'
	| 'Accepted Invalid'
	| 'Refused'
	| 'Overridden';

export interface KeyenceValidationFinding {
	id: string;
	code: string;
	severity: KeyenceValidationSeverity;
	program_label: string | null;
	balloon: string | null;
	balloon_number: number | null;
	field: string | null;
	expected_value: string | null;
	actual_value: string | null;
	suggested_balloon_number: number | null;
	/** Set on a missing-balloon finding: the misnamed measurement that is
	 *  probably this balloon recorded under the wrong name. */
	suggested_program_label: string | null;
	confidence: string | null;
	confidence_band: string | null;
	alternatives: { balloon_number: number; confidence: number }[];
	message: string;
	is_false_positive?: boolean;
	false_positive_marked_time?: string | null;
}

/** The compact report returned by the upload endpoint, per file. */
export interface KeyenceValidationSummary {
	report_id: string | null;
	file_name: string | null;
	view: string | null;
	outcome: KeyenceValidationOutcome;
	error_count: number;
	warning_count: number;
	matched_count: number;
	plan_balloon_count: number;
	program_measurement_count: number;
	parse_error: string | null;
	findings: (Omit<KeyenceValidationFinding, 'id' | 'balloon' | 'confidence'> & {
		confidence: number | null;
	})[];
}

export interface KeyenceValidationReport {
	id: string;
	program: string | null;
	program_file_name: string | null;
	inspection_plan: string;
	ballooning_project_id: string | null;
	order_name: string | null;
	part_name: string | null;
	keyence_device: string | null;
	keyence_device_name: string | null;
	view: string | null;
	file_name: string;
	file_sha256: string;
	file_size: number;
	source: string;
	outcome: KeyenceValidationOutcome;
	enforcement: KeyenceValidationEnforcement;
	override_reason: string | null;
	error_count: number;
	warning_count: number;
	program_measurement_count: number;
	plan_balloon_count: number;
	matched_count: number;
	undecoded_measurement_count: number;
	unit: string | null;
	parser_version: string | null;
	parser_schema_version: number | null;
	parse_ms: number | null;
	parse_error: string | null;
	created_time: string;
	uploaded_by_name: string | null;
	/** List rows only. */
	finding_codes?: string[];
	/** Detail rows only. */
	findings?: KeyenceValidationFinding[];
}

export interface PaginatedKeyenceValidationReports {
	count: number;
	next: string | null;
	previous: string | null;
	results: KeyenceValidationReport[];
}

export interface KeyenceValidationStats {
	total: number;
	by_outcome: Partial<Record<KeyenceValidationOutcome, number>>;
	by_enforcement: Partial<Record<KeyenceValidationEnforcement, number>>;
	pass_rate: number | null;
	top_finding_codes: {
		code: string;
		severity: KeyenceValidationSeverity;
		count: number;
	}[];
	false_positives: number;
	by_device: { name: string; total: number; failed: number }[];
	by_source: { source: string; total: number; failed: number }[];
	by_uploader: {
		user_id: number | null;
		name: string;
		total: number;
		failed: number;
	}[];
	daily_trend: {
		date: string | null;
		total: number;
		passed: number;
		failed: number;
	}[];
}

export type KeyenceComparisonStatus =
	| 'match'
	| 'mismatch'
	| 'missing_from_program'
	| 'not_checked'
	| 'not_in_plan'
	| 'misnamed'
	| 'probable_rename'
	| 'duplicate';

export interface KeyenceComparisonFinding {
	code: string;
	severity: KeyenceValidationSeverity;
	field: string | null;
	message: string;
	expected_value: string | null;
	actual_value: string | null;
	suggested_balloon_number: number | null;
	suggested_program_label: string | null;
	confidence_band: string | null;
	alternatives: { balloon_number: number; confidence: number }[];
}

export interface KeyenceComparisonProgramSide {
	measurement_type: string | null;
	nominal: string | null;
	tolerance_plus: string | null;
	tolerance_minus: string | null;
}

export interface KeyenceComparisonPlanSide {
	balloon_id: string;
	gdt_type: string | null;
	nominal: string | null;
	tolerance_plus: string | null;
	tolerance_minus: string | null;
}

export interface KeyenceValidationComparisonRow {
	key: string;
	status: KeyenceComparisonStatus;
	balloon_number: number | null;
	program_label: string | null;
	program: KeyenceComparisonProgramSide | null;
	plan: KeyenceComparisonPlanSide | null;
	mismatched_fields: string[];
	findings: KeyenceComparisonFinding[];
}

export interface KeyenceValidationComparison {
	report_id: string;
	/** Unit the displayed values are in, which differs from `declared_unit`
	 *  when validation proved the plan's unit label wrong. */
	unit: string | null;
	declared_unit: string | null;
	values_comparable: boolean;
	plan_modified_since_validation: boolean;
	rows: KeyenceValidationComparisonRow[];
	report_findings: KeyenceComparisonFinding[];
}

export interface KeyenceValidationReportQuery {
	page?: number;
	page_size?: number;
	search?: string;
	outcome?: string;
	enforcement?: string;
	source?: string;
	keyence_device?: string;
	code?: string;
	severity?: string;
	view?: string;
	inspection_plan?: string;
	program?: string;
	created_by?: string;
	created_after?: string;
	created_before?: string;
	ordering?: string;
}

export enum CalibrationStatusValue {
	IN_CALIBRATION = "in_calibration",
	OUT_OF_CALIBRATION = "out_of_calibration",
}

export enum CalibrationStatusLabel {
	IN_CALIBRATION = "In Calibration",
	OUT_OF_CALIBRATION = "Out of Calibration",
}


export const CALIBRATION_LABELS = {
	[CalibrationStatusValue.IN_CALIBRATION]: CalibrationStatusLabel.IN_CALIBRATION,
	[CalibrationStatusValue.OUT_OF_CALIBRATION]: CalibrationStatusLabel.OUT_OF_CALIBRATION,
}

export enum CalibrationStrategyValue {
	ON_DEMAND = "on_demand",
	ANNUAL = "annual",
}

export enum CalibrationStrategyLabel {
	ON_DEMAND = "On Demand",
	ANNUAL = "Annual",
}

export const CALIBRATION_STRATEGY_LABELS = {
	[CalibrationStrategyValue.ON_DEMAND]: CalibrationStrategyLabel.ON_DEMAND,
	[CalibrationStrategyValue.ANNUAL]: CalibrationStrategyLabel.ANNUAL,
}

export enum GageStatusValue {
	IN_SERVICE = "in_service",
	REMOVED_FROM_SERVICE = "removed_from_service",
	FOR_REFERENCE_ONLY = "for_reference_only",
}

export enum GageStatusLabel {
	IN_SERVICE = "In Service",
	REMOVED_FROM_SERVICE = "Removed from Service",
	FOR_REFERENCE_ONLY = "For Reference Only",
}

export enum GageRemovalReasonValue {
	DROPPED = "dropped",
	DAMAGED = "damaged",
	LOST = "lost",
	OUT_OF_CALIBRATION = "out_of_calibration",
	RETIRED = "retired",
	OTHER = "other",
}

export const GAGE_REMOVAL_REASON_OPTIONS: { value: GageRemovalReasonValue; label: string }[] = [
	{ value: GageRemovalReasonValue.DROPPED, label: "Dropped" },
	{ value: GageRemovalReasonValue.DAMAGED, label: "Damaged" },
	{ value: GageRemovalReasonValue.LOST, label: "Lost" },
	{ value: GageRemovalReasonValue.OUT_OF_CALIBRATION, label: "Out of Calibration" },
	{ value: GageRemovalReasonValue.RETIRED, label: "Retired" },
	{ value: GageRemovalReasonValue.OTHER, label: "Other" },
];

export const GAGE_STATUS_LABELS = {
	[GageStatusValue.IN_SERVICE]: GageStatusLabel.IN_SERVICE,
	[GageStatusValue.REMOVED_FROM_SERVICE]: GageStatusLabel.REMOVED_FROM_SERVICE,
	[GageStatusValue.FOR_REFERENCE_ONLY]: GageStatusLabel.FOR_REFERENCE_ONLY,
}
